import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as api from "../../api/index";
import { IoMdArrowBack } from "react-icons/io";
import { FiPackage, FiTruck, FiCheckCircle } from "react-icons/fi";

const TrackOrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await api.gettrackOrderById(id);
        setOrder(res.data);
      } catch (err) {
        setError("Failed to load order.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-orange-600"></div>
    </div>
  );
  
  if (error) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6 text-center">
      <p className="text-red-500 font-bold uppercase tracking-widest text-xs mb-4">{error}</p>
      <button onClick={() => navigate(-1)} className="bg-gray-900 text-white px-6 py-2 rounded-xl text-xs font-black uppercase">Go Back</button>
    </div>
  );

  if (!order) return <p className="text-center mt-10 font-black uppercase text-gray-400">No order found.</p>;

  return (
    <div className="flex flex-col min-h-screen bg-[#F8F9FA] relative">
      {/* --- Premium Header --- */}
      <div className="bg-white px-6 py-6 flex items-center shadow-sm border-b border-gray-100">
        <button
          onClick={() => navigate(-1)}
          className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition text-gray-700"
        >
          <IoMdArrowBack size={24} />
        </button>
        <div className="ml-4">
          <h1 className="text-xs font-black uppercase tracking-[0.2em] text-orange-600">Tracking Details</h1>
          <p className="text-lg font-black text-gray-900 tracking-tighter truncate max-w-[200px]">{order.item}</p>
        </div>
      </div>

      {/* --- Main Content Area --- */}
      <div className="flex-1 p-6 max-w-2xl mx-auto w-full">
        <div className="bg-white rounded-[2.5rem] p-8 shadow-xl shadow-gray-200/50 border border-gray-100">
          
          <div className="flex items-center justify-between mb-10 pb-6 border-b border-dashed border-gray-200">
             <div>
                <p className="text-[10px] font-black text-gray-400 uppercase mb-1">Order Identifier</p>
                <p className="font-mono text-sm font-bold text-gray-700">#{id.slice(-8).toUpperCase()}</p>
             </div>
             <div className="w-12 h-12 bg-orange-50 text-orange-600 rounded-2xl flex items-center justify-center">
                <FiPackage size={24} />
             </div>
          </div>

          {/* --- Timeline Logic --- */}
          <div className="relative">
            {order.tracking.map((step, idx) => {
              const isLast = idx === order.tracking.length - 1;
              return (
                <div key={idx} className="relative flex items-start group">
                  
                  {/* Vertical Line Connector */}
                  {!isLast && (
                    <div 
                      className={`absolute left-[10px] top-6 w-[2px] h-full -ml-[1px]
                        ${step.done && order.tracking[idx + 1]?.done ? "bg-green-500" : "bg-gray-200"}
                      `}
                    />
                  )}

                  {/* Status Indicator Dot */}
                  <div className="relative z-10">
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center transition-all duration-500
                        ${step.done 
                          ? "bg-green-500 text-white scale-110 shadow-lg shadow-green-100" 
                          : "bg-white border-2 border-gray-200 text-transparent"}
                      `}
                    >
                      {step.done && <FiCheckCircle size={12} strokeWidth={4} />}
                    </div>
                  </div>

                  {/* Step Metadata */}
                  <div className="ml-6 mb-10 group-last:mb-0">
                    <p
                      className={`text-sm font-black uppercase tracking-tight transition-colors
                        ${step.done ? "text-gray-900" : "text-gray-400"}
                      `}
                    >
                      {step.stage}
                    </p>
                    
                    {step.date ? (
                      <div className="mt-1 flex items-center gap-2">
                         <span className="text-[10px] font-bold text-gray-400 bg-gray-50 px-2 py-0.5 rounded-md">
                           {new Date(step.date).toLocaleDateString()}
                         </span>
                         <span className="text-[10px] font-bold text-gray-400 bg-gray-50 px-2 py-0.5 rounded-md">
                           {new Date(step.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                         </span>
                      </div>
                    ) : (
                      <p className="text-[10px] font-bold text-gray-300 uppercase tracking-widest mt-1 italic">Pending Update</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* --- Help Footer --- */}
        <div className="mt-8 text-center">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-loose">
                Updates may take up to 30 mins to reflect.<br/> Contact support if order is stalled.
            </p>
        </div>
      </div>
    </div>
  );
};

export default TrackOrderDetails;